import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Link
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Person as PersonIcon,
  PersonOutline as LeadIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  collectionGroup,
  Timestamp 
} from 'firebase/firestore';
import { AuthContext } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import ContactsFilters from '../../components/Contacts/ContactsFilters';
import ContactForm from '../../components/Contacts/ContactForm';
import { 
  getEntityTypeLabel, 
  getContactTypeLabel, 
  getMeetingOutcomeLabel,
  getContactTypeColor,
  getMeetingOutcomeColor,
  getEntityTypeColor
} from '../../constants/contactsConstants';

const ContactsPage = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  
  const [openContactForm, setOpenContactForm] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Filtry
  const [filters, setFilters] = useState({
    search: '',
    employee: 'all',
    entityType: 'all',
    contactType: 'all',
    meetingOutcome: 'all',
    entityId: 'all',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    fetchContacts();
    if (isAdmin) {
      fetchEmployees();
    }
    fetchClients();
    fetchLeads();
  }, [currentUser, isAdmin, filters]);

  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
    }
  };

  const fetchClients = async () => {
    try {
      // Pobierz tylko podstawowe dane klientów (id, imię, nazwisko)
      const clientsQuery = query(collection(db, 'clients'));
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName || '',
        lastName: doc.data().lastName || ''
      }));
      setClients(clientsList);
    } catch (error) {
      console.error('Błąd podczas pobierania klientów:', error);
      // Nie wyświetlaj błędu użytkownikowi, kontynuuj z pustą listą klientów
      setClients([]);
    }
  };

  const fetchLeads = async () => {
    try {
      // Pobierz tylko podstawowe dane leadów (id, imię, nazwisko)
      const leadsQuery = query(collection(db, 'leads'));
      const leadsSnapshot = await getDocs(leadsQuery);
      const leadsList = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName || '',
        lastName: doc.data().lastName || ''
      }));
      setLeads(leadsList);
    } catch (error) {
      console.error('Błąd podczas pobierania leadów:', error);
      // Nie wyświetlaj błędu użytkownikowi, kontynuuj z pustą listą leadów
      setLeads([]);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      
      let allContacts = [];
      
      // 1. Pobierz kontakty z kolekcji contacts
      try {
        let clientContactsQuery;
        
        if (isAdmin) {
          // Administrator widzi wszystkie kontakty
          clientContactsQuery = query(
            collection(db, 'contacts'),
            orderBy('contactDate', 'desc')
          );
        } else {
          // Zwykły pracownik widzi tylko swoje kontakty
          clientContactsQuery = query(
            collection(db, 'contacts'),
            where('employeeId', '==', currentUser.uid),
            orderBy('contactDate', 'desc')
          );
        }
        
        const clientContactsSnapshot = await getDocs(clientContactsQuery);
        const clientContactsList = clientContactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          contactDate: doc.data().contactDate?.toDate() || new Date(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          entityType: 'client', // Upewnij się, że mamy entityType
          source: 'contacts' // Dodaj informację o źródle
        }));
        
        allContacts = [...allContacts, ...clientContactsList];
      } catch (error) {
        console.error('Błąd podczas pobierania kontaktów klientów:', error);
        // Kontynuuj z pozostałymi zapytaniami
      }
      
      // 2. Pobierz komentarze z subkolekcji leads/{leadId}/comments
      try {
        let leadCommentsQuery;
        
        if (isAdmin) {
          // Administrator widzi wszystkie komentarze
          leadCommentsQuery = query(
            collectionGroup(db, 'comments'),
            orderBy('contactDate', 'desc')
          );
        } else {
          // Zwykły pracownik widzi tylko swoje komentarze
          leadCommentsQuery = query(
            collectionGroup(db, 'comments'),
            where('employeeId', '==', currentUser.uid),
            orderBy('contactDate', 'desc')
          );
        }
        
        const leadCommentsSnapshot = await getDocs(leadCommentsQuery);
        const leadCommentsList = leadCommentsSnapshot.docs.map(doc => {
          // Pobierz leadId z ścieżki dokumentu
          const leadId = doc.ref.parent.parent.id;
          
          return {
            id: doc.id,
            ...doc.data(),
            contactDate: doc.data().contactDate?.toDate() || new Date(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            entityType: 'lead', // Upewnij się, że mamy entityType
            entityId: leadId, // Ustaw entityId na ID leada
            source: 'comments' // Dodaj informację o źródle
          };
        });
        
        allContacts = [...allContacts, ...leadCommentsList];
      } catch (error) {
        console.error('Błąd podczas pobierania komentarzy leadów:', error);
        // Kontynuuj z już pobranymi danymi
      }
      
      // 3. Zastosuj filtry
      allContacts = applyContactsFilters(allContacts);
      
      // 4. Posortuj po dacie kontaktu (malejąco)
      allContacts.sort((a, b) => b.contactDate - a.contactDate);
      
      setContacts(allContacts);
    } catch (error) {
      console.error('Błąd podczas pobierania kontaktów:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania danych kontaktów',
        severity: 'error'
      });
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const applyContactsFilters = (contactsList) => {
    return contactsList.filter(contact => {
      // Filtr wyszukiwania
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const notes = (contact.notes || '').toLowerCase();
        
        if (!notes.includes(searchTerm)) {
          return false;
        }
      }

      // Filtr pracownika
      if (isAdmin && filters.employee !== 'all' && contact.employeeId !== filters.employee) {
        return false;
      }

      // Filtr typu podmiotu
      if (filters.entityType !== 'all' && contact.entityType !== filters.entityType) {
        return false;
      }

      // Filtr typu kontaktu
      if (filters.contactType !== 'all' && contact.contactType !== filters.contactType) {
        return false;
      }

      // Filtr wyniku spotkania
      if (filters.meetingOutcome !== 'all') {
        if (filters.meetingOutcome === 'has_outcome' && !contact.meetingOutcome) {
          return false;
        }
        if (filters.meetingOutcome === 'no_outcome' && contact.meetingOutcome) {
          return false;
        }
        if (filters.meetingOutcome !== 'has_outcome' && filters.meetingOutcome !== 'no_outcome' && 
            contact.meetingOutcome !== filters.meetingOutcome) {
          return false;
        }
      }

      // Filtr konkretnego podmiotu
      if (filters.entityId !== 'all' && contact.entityId !== filters.entityId) {
        return false;
      }

      // Filtr zakresu dat
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        if (contact.contactDate < fromDate) {
          return false;
        }
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Koniec dnia
        if (contact.contactDate > toDate) {
          return false;
        }
      }

      return true;
    });
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setOpenContactForm(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setOpenContactForm(true);
  };

  const handleContactFormSubmit = (contactData) => {
    setOpenContactForm(false);
    fetchContacts(); // Odśwież listę kontaktów
    
    setSnackbar({
      open: true,
      message: selectedContact ? 'Kontakt został zaktualizowany' : 'Kontakt został dodany',
      severity: 'success'
    });
  };

  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date instanceof Date ? date : date.toDate(), 'dd.MM.yyyy HH:mm', { locale: pl });
  };

  const getEntityName = (entityType, entityId) => {
    if (entityType === 'client') {
      const client = clients.find(c => c.id === entityId);
      return client ? `${client.firstName} ${client.lastName}` : entityId;
    } else if (entityType === 'lead') {
      const lead = leads.find(l => l.id === entityId);
      return lead ? `${lead.firstName} ${lead.lastName}` : entityId;
    }
    return entityId;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : employeeId;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Kontakty i interakcje
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddContact}
        >
          Dodaj kontakt
        </Button>
      </Box>

      <ContactsFilters 
        filters={filters} 
        onFilterChange={setFilters} 
        employees={employees} 
        clients={clients}
        leads={leads}
        isAdmin={isAdmin}
      />

      {/* Tabela kontaktów */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data kontaktu</TableCell>
              <TableCell>Typ kontaktu</TableCell>
              <TableCell>Podmiot</TableCell>
              <TableCell>Typ podmiotu</TableCell>
              {isAdmin && <TableCell>Pracownik</TableCell>}
              <TableCell>Notatki</TableCell>
              <TableCell>Wynik spotkania</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Brak kontaktów spełniających kryteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow key={`${contact.source}-${contact.id}`} hover>
                  <TableCell>{formatDate(contact.contactDate)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getContactTypeLabel(contact.contactType)} 
                      color={getContactTypeColor(contact.contactType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {contact.entityType === 'client' ? (
                        <Link href={`/clients/${contact.entityId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {getEntityName(contact.entityType, contact.entityId)}
                        </Link>
                      ) : (
                        <Link href={`/leads/${contact.entityId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                          <LeadIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {getEntityName(contact.entityType, contact.entityId)}
                        </Link>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getEntityTypeLabel(contact.entityType)} 
                      color={getEntityTypeColor(contact.entityType)}
                      size="small"
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell>{getEmployeeName(contact.employeeId)}</TableCell>
                  )}
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 250, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}
                    >
                      {contact.notes}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {contact.meetingOutcome ? (
                      <Chip 
                        label={getMeetingOutcomeLabel(contact.meetingOutcome)} 
                        color={getMeetingOutcomeColor(contact.meetingOutcome)}
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edytuj">
                        <IconButton 
                          size="small"
                          onClick={() => handleEditContact(contact)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zobacz szczegóły">
                        <IconButton 
                          size="small"
                          onClick={() => handleEditContact(contact)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog formularza kontaktu */}
      <Dialog
        open={openContactForm}
        onClose={() => setOpenContactForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedContact ? 'Edytuj kontakt' : 'Dodaj nowy kontakt'}
        </DialogTitle>
        <DialogContent>
          <ContactForm 
            initialData={selectedContact}
            onSubmit={handleContactFormSubmit}
            onCancel={() => setOpenContactForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactsPage;
